import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createElementBlock as _createElementBlock, unref as _unref, Transition as _Transition } from "vue"

const _hoisted_1 = {
  key: 0,
  "data-cy": "lien-form-panel",
  class: "fixed top-20 right-0 bg-white shadow-2xl z-[200] flex flex-col rounded-lg overflow-hidden",
  style: {"width":"40vw","min-width":"480px","max-width":"700px","height":"calc(100vh - 80px)"}
}
const _hoisted_2 = { class: "flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white" }
const _hoisted_3 = { class: "text-lg font-bold text-gray-900" }
const _hoisted_4 = { class: "flex-1 overflow-y-auto px-6 py-5" }
const _hoisted_5 = { class: "mb-5" }
const _hoisted_6 = {
  key: 0,
  class: "mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4"
}
const _hoisted_7 = { class: "flex items-center gap-2" }
const _hoisted_8 = { class: "mb-5" }
const _hoisted_9 = { class: "grid grid-cols-2 gap-4 mb-5" }
const _hoisted_10 = {
  key: 0,
  class: "mb-5 rounded-lg overflow-hidden",
  "data-cy": "savings-preview"
}
const _hoisted_11 = {
  key: 0,
  class: "bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-4 py-3 flex items-center justify-between"
}
const _hoisted_12 = { class: "flex items-center gap-2" }
const _hoisted_13 = { class: "w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" }
const _hoisted_14 = { class: "text-base font-bold text-green-dark tabular-nums leading-tight" }
const _hoisted_15 = { class: "text-right" }
const _hoisted_16 = { class: "text-2xl font-bold text-green-dark tabular-nums leading-none" }
const _hoisted_17 = {
  key: 1,
  class: "bg-[rgba(var(--v-theme-error),0.08)] border border-[rgba(var(--v-theme-error),0.3)] rounded-lg px-4 py-3 flex items-center justify-between"
}
const _hoisted_18 = { class: "flex items-center gap-2" }
const _hoisted_19 = { class: "w-8 h-8 rounded-full bg-[rgba(var(--v-theme-error),0.08)] flex items-center justify-center" }
const _hoisted_20 = { class: "text-base font-bold text-error tabular-nums leading-tight" }
const _hoisted_21 = { class: "text-right" }
const _hoisted_22 = { class: "text-2xl font-bold text-error tabular-nums leading-none" }
const _hoisted_23 = { class: "mb-5" }
const _hoisted_24 = { class: "flex justify-end mt-1" }
const _hoisted_25 = { class: "grid grid-cols-2 gap-4 mb-1" }
const _hoisted_26 = {
  key: 0,
  class: "text-xs text-error mb-4",
  "data-cy": "lien-date-of-service-end-error"
}
const _hoisted_27 = {
  key: 1,
  class: "mb-5"
}
const _hoisted_28 = { class: "grid grid-cols-2 gap-4 mb-5" }
const _hoisted_29 = { class: "mb-5" }
const _hoisted_30 = { class: "px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0 mr-16" }
const _hoisted_31 = { class: "flex flex-col items-end gap-0.5" }
const _hoisted_32 = {
  key: 0,
  class: "text-xs text-gray-400"
}

import { ref, reactive, watch, computed } from 'vue';
import { useLienStore } from '@/stores/useLienStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { formatCurrency } from '@/services/utils';
import { LIEN_TYPE_OPTIONS } from '@/constants/lienConstants';
import type { LienData, LienType, VendorData, CreateLienRequest } from '@/models/lienTypes';
import { validateServiceDateRange, isPushed } from '@/utils/lienHelpers';
import { parseISO, format, isValid } from 'date-fns';
import LlTextField from '@/components/LlTextField.vue';
import LlTextarea from '@/components/LlTextarea.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlAutocomplete from '@/components/LlAutocomplete.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlIcon from '@/components/LlIcon.vue';
import SkeletonRenderer from '@/components/SkeletonRenderer.vue';
import EventTracker from '@/services/eventTracker';

// ── Types ──────────────────────────────────────────────────────────────────
interface FormState {
  vendorId: string | null;
  lienType: LienType | null;
  originalAmount: number | null;
  negotiatedAmount: number | null;
  description: string;
  dateOfService: string | null;
  dateOfServiceEnd: string | null;
  recordedFilingDate: string | null;
  county: string;
}

// ── Props / Emits ──────────────────────────────────────────────────────────

export default /*@__PURE__*/_defineComponent({
  __name: 'LienFormPanel',
  props: {
    modelValue: { type: Boolean },
    matterId: {},
    matterName: {},
    lien: {},
    tracking: {},
    loading: { type: Boolean }
  },
  emits: ['update:modelValue', 'saved'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// ── Dependencies ───────────────────────────────────────────────────────────
const lienStore = useLienStore();
const { handleError, handleSuccess } = useSnackbarHandler();

// ── Template refs ──────────────────────────────────────────────────────────
const formRef = ref<{ validate: () => Promise<{ valid: boolean }>; resetValidation: () => void } | null>(null);
const payeeAutocompleteRef = ref<InstanceType<typeof LlAutocomplete> | null>(null);

// ── Form state ─────────────────────────────────────────────────────────────
const EMPTY_FORM: FormState = {
  vendorId: null,
  lienType: null,
  originalAmount: null,
  negotiatedAmount: null,
  description: '',
  dateOfService: null,
  dateOfServiceEnd: null,
  recordedFilingDate: null,
  county: '',
};

const form: FormState = reactive({ ...EMPTY_FORM });
const vendorIdError = ref('');
const saving = ref(false);

const isEditing = computed(() => !!props.lien?.lienId);
const isPushedToQbo = computed(() => isPushed(props.lien?.status));

// ── Date helpers (LlDatePicker ↔ ISO string) ──────────────────────────────
function toDate(iso: string | null): Date | null {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
}
function fromDate(d: Date | null): string | null {
  return d && isValid(d) ? format(d, 'yyyy-MM-dd') : null;
}
const dateOfServiceDate = computed({
  get: () => toDate(form.dateOfService),
  set: (v: Date | null) => { form.dateOfService = fromDate(v); },
});
const dateOfServiceEndDate = computed({
  get: () => toDate(form.dateOfServiceEnd),
  set: (v: Date | null) => { form.dateOfServiceEnd = fromDate(v); },
});
const recordedFilingDateDate = computed({
  get: () => toDate(form.recordedFilingDate),
  set: (v: Date | null) => { form.recordedFilingDate = fromDate(v); },
});

// Inline validation for the optional service date range (end ≥ start).
const serviceEndDateError = computed(() =>
  validateServiceDateRange(form.dateOfService, form.dateOfServiceEnd),
);

// ── Vendor search ──────────────────────────────────────────────────────────
const vendors = ref<VendorData[]>([]);
const vendorsLoading = ref(false);
const vendorSearch = ref('');
const vendorError = ref('');

let vendorSearchTimer: ReturnType<typeof setTimeout> | null = null;

function onVendorSearch(val: string): void {
  if (vendorSearchTimer) clearTimeout(vendorSearchTimer);
  vendorError.value = '';
  vendorSearchTimer = setTimeout(async () => {
    vendorsLoading.value = true;
    try {
      vendors.value = await lienStore.fetchVendors(val);
    } catch {
      vendorError.value = 'Unable to load vendors — check QuickBooks connection in Settings.';
    } finally {
      vendorsLoading.value = false;
    }
  }, 250);
}

// ── Inline add vendor ──────────────────────────────────────────────────────
const showAddVendor = ref(false);
const newVendorName = ref('');
const creatingVendor = ref(false);
const vendorCreatedInline = ref(false);

function openAddVendor(): void {
  payeeAutocompleteRef.value?.$el?.querySelector('input')?.blur();
  newVendorName.value = vendorSearch.value;
  showAddVendor.value = true;
}

function cancelAddVendor(): void {
  showAddVendor.value = false;
  newVendorName.value = '';
}

async function saveNewVendor(): Promise<void> {
  const name = newVendorName.value.trim();
  if (!name) return;
  creatingVendor.value = true;
  try {
    const vendor = await lienStore.addVendor({ displayName: name });
    vendors.value = [...vendors.value, vendor];
    form.vendorId = vendor.vendorId;
    vendorCreatedInline.value = true;
    cancelAddVendor();
    handleSuccess(`Vendor "${vendor.displayName}" created`);
  } catch (e: unknown) {
    handleError(e);
  } finally {
    creatingVendor.value = false;
  }
}

// ── Validation rules ───────────────────────────────────────────────────────
const negotiatedAmountRules = computed(() => [
  (v: number | null | string) =>
    v === null || v === '' || Number(v) >= 0 || 'Negotiated amount cannot be negative',
  (v: number | null | string) =>
    v === null ||
    v === '' ||
    !form.originalAmount ||
    Number(v) <= Number(form.originalAmount) ||
    'Negotiated amount cannot exceed original amount',
]);

// ── Savings preview ────────────────────────────────────────────────────────
const savingsPreview = computed(() => {
  const orig = Number(form.originalAmount) || 0;
  const neg = Number(form.negotiatedAmount) || 0;
  if (orig <= 0 || neg <= 0) return { show: false, over: false, amount: 0, percentage: 0 };

  const diff = orig - neg;
  const percentage = ((Math.abs(diff) / orig) * 100).toFixed(2);
  return {
    show: true,
    over: neg > orig,
    amount: Math.abs(diff),
    percentage,
  };
});

// ── Description placeholder ────────────────────────────────────────────────
const descriptionPlaceholder = computed(() => {
  const vendor = vendors.value.find((v) => v.vendorId === form.vendorId)?.displayName;
  const lienTypeLabel = LIEN_TYPE_OPTIONS.find((o) => o.id === form.lienType)?.label;
  const parts = [props.matterName, vendor, lienTypeLabel].filter(Boolean);
  if (parts.length === 0) return 'e.g. Matter Name – Payee – Type';
  return parts.join(' – ');
});

// ── Watchers ───────────────────────────────────────────────────────────────

// Populate form when editing
watch(
  () => props.lien,
  (lien) => {
    if (lien) {
      form.vendorId = lien.vendorId;
      form.lienType = lien.lienType;
      form.originalAmount = lien.originalAmount;
      form.negotiatedAmount = lien.negotiatedAmount ?? null;
      form.description = lien.description ?? '';
      form.dateOfService = lien.dateOfService?.substring(0, 10) ?? null;
      form.dateOfServiceEnd = lien.dateOfServiceEnd?.substring(0, 10) ?? null;
      form.recordedFilingDate = lien.recordedFilingDate?.substring(0, 10) ?? null;
      form.county = lien.county ?? '';
      if (lien.vendorId && lien.vendorDisplayName) {
        vendors.value = [{ vendorId: lien.vendorId, displayName: lien.vendorDisplayName }];
      }
    } else {
      Object.assign(form, EMPTY_FORM);
    }
  },
  { immediate: true },
);

// Clear payee error when vendor is selected
watch(() => form.vendorId, (id) => { if (id) vendorIdError.value = ''; });

// Reset on open
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return;
    vendorIdError.value = '';
    formRef.value?.resetValidation();
    showAddVendor.value = false;
    newVendorName.value = '';
    vendorCreatedInline.value = false;
    if (!props.lien) {
      Object.assign(form, EMPTY_FORM);
      vendors.value = [];
      vendorSearch.value = '';
    }
    onVendorSearch('');
  },
);

// ── Validate / Submit ──────────────────────────────────────────────────────

function toNullableNumber(v: number | null | string): number | null {
  return v !== null && v !== '' ? Number(v) : null;
}

async function validate(): Promise<boolean> {
  let valid = true;

  const formResult = await formRef.value?.validate?.();
  if (formResult && !formResult.valid) valid = false;

  // LlAutocomplete has no :rules — validate manually
  vendorIdError.value = '';
  if (!form.vendorId) {
    vendorIdError.value = 'Payee is required';
    valid = false;
  }

  // Submit guards (mirror :rules for environments without v-form)
  if (!form.lienType) valid = false;
  if (!form.originalAmount || Number(form.originalAmount) <= 0) valid = false;
  const neg = toNullableNumber(form.negotiatedAmount);
  if (neg !== null) {
    if (neg < 0 || neg > Number(form.originalAmount)) valid = false;
  }
  if (form.description && form.description.length > 500) valid = false;
  if (form.county && form.county.length > 255) valid = false;
  if (serviceEndDateError.value) valid = false;

  return valid;
}

function buildPayload(): CreateLienRequest {
  return {
    vendorId: form.vendorId!,
    lienType: form.lienType!,
    originalAmount: Number(form.originalAmount),
    negotiatedAmount: toNullableNumber(form.negotiatedAmount),
    description: form.description || null,
    county: form.county || null,
    dateOfService: form.dateOfService || null,
    dateOfServiceEnd: form.dateOfServiceEnd || null,
    recordedFilingDate: form.recordedFilingDate || null,
  };
}

function trackEditAnalytics(payload: CreateLienRequest): void {
  if (!props.tracking) return;
  const lien = props.lien!;

  if (Number(lien.originalAmount) !== payload.originalAmount) {
    EventTracker.trackEvent(props.tracking, 'Lien', 'Amount Updated', {
      matterId: props.matterId,
      lienId: lien.lienId,
      field: 'originalAmount',
      oldValue: lien.originalAmount,
      newValue: payload.originalAmount,
    });
  }

  const oldNeg = lien.negotiatedAmount ?? null;
  const newNeg = payload.negotiatedAmount ?? null;
  if (oldNeg !== newNeg) {
    EventTracker.trackEvent(props.tracking, 'Lien', 'Amount Updated', {
      matterId: props.matterId,
      lienId: lien.lienId,
      field: 'negotiatedAmount',
      oldValue: oldNeg,
      newValue: newNeg,
    });
  }
}

function trackCreateAnalytics(payload: CreateLienRequest): void {
  if (!props.tracking) return;
  EventTracker.trackEvent(props.tracking, 'Lien', 'Created', {
    matterId: props.matterId,
    lienType: payload.lienType,
    hasNegotiatedAmount: payload.negotiatedAmount !== null,
    hasCounty: !!payload.county,
    source: 'manual',
    vendorCreatedInline: vendorCreatedInline.value,
  });
  vendorCreatedInline.value = false;
}

async function submit(): Promise<void> {
  if (isPushedToQbo.value) return;
  if (!(await validate())) return;

  saving.value = true;
  try {
    const payload = buildPayload();
    let saved: LienData;

    if (isEditing.value) {
      saved = await lienStore.updateLien(props.lien!.lienId, payload);
      trackEditAnalytics(payload);
    } else {
      saved = await lienStore.createLien(props.matterId, payload);
      trackCreateAnalytics(payload);
    }

    handleSuccess(isEditing.value ? 'Lien updated' : 'Lien added');
    emit('saved', saved);
    close();
  } catch (e: unknown) {
    handleError(e);
  } finally {
    saving.value = false;
  }
}

function close(): void {
  emit('update:modelValue', false);
}

return (_ctx: any,_cache: any) => {
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_Transition, {
    name: "lien-panel",
    "enter-active-class": "lien-panel-enter-active",
    "leave-active-class": "lien-panel-leave-active",
    "enter-from-class": "lien-panel-enter-from",
    "enter-to-class": "lien-panel-enter-to",
    "leave-from-class": "lien-panel-leave-from",
    "leave-to-class": "lien-panel-leave-to"
  }, {
    default: _withCtx(() => [
      (__props.modelValue)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("h2", _hoisted_3, _toDisplayString((isEditing.value || __props.loading) ? 'Edit Lien' : 'Add Lien'), 1),
              _createVNode(LlSecondaryButton, {
                icon: "mdi-close",
                variant: "text",
                size: "small",
                "aria-label": "Close",
                "data-cy": "lien-panel-close",
                onClick: close
              })
            ]),
            _createElementVNode("div", _hoisted_4, [
              (__props.loading)
                ? (_openBlock(), _createBlock(SkeletonRenderer, {
                    key: 0,
                    spec: [
              { type: 'list-item', class: 'mb-1' },
              { type: 'list-item', class: 'mb-1' },
              { type: 'row', spacing: 'gap-4', class: 'mb-1', columns: [
                { type: 'list-item', class: 'flex-1' },
                { type: 'list-item', class: 'flex-1' },
              ]},
              { type: 'paragraph', class: 'mb-1' },
              { type: 'row', spacing: 'gap-4', class: 'mb-1', columns: [
                { type: 'list-item', class: 'flex-1' },
                { type: 'list-item', class: 'flex-1' },
              ]},
              { type: 'list-item' },
            ],
                    "container-class": "space-y-0"
                  }))
                : (_openBlock(), _createBlock(_component_v_form, {
                    key: 1,
                    ref_key: "formRef",
                    ref: formRef
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_5, [
                        _createVNode(LlAutocomplete, {
                          ref_key: "payeeAutocompleteRef",
                          ref: payeeAutocompleteRef,
                          "model-value": form.vendorId,
                          search: vendorSearch.value,
                          label: "Payee",
                          "is-required": true,
                          items: vendors.value,
                          "item-title": "displayName",
                          "item-value": "vendorId",
                          loading: vendorsLoading.value,
                          placeholder: "Search vendors…",
                          "hide-details": "auto",
                          "error-messages": vendorIdError.value,
                          clearable: "",
                          "data-cy": "lien-payee",
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (form.vendorId = $event)),
                          "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => {vendorSearch.value = $event; onVendorSearch($event)})
                        }, {
                          "no-data": _withCtx(() => [
                            _createVNode(_component_v_list_item, null, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_list_item_title, {
                                  class: _normalizeClass(["text-sm", vendorError.value ? 'text-error' : 'text-gray-500'])
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(vendorError.value || 'No vendors found'), 1)
                                  ]),
                                  _: 1
                                }, 8, ["class"])
                              ]),
                              _: 1
                            })
                          ]),
                          "append-item": _withCtx(() => [
                            _createVNode(_component_v_list_item, {
                              class: "text-primary cursor-pointer",
                              "data-cy": "add-new-vendor",
                              onClick: openAddVendor
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_list_item_title, { class: "font-medium" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, {
                                      size: "16",
                                      class: "mr-1"
                                    }, {
                                      default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                        _createTextVNode("mdi-plus", -1)
                                      ]))]),
                                      _: 1
                                    }),
                                    _createTextVNode(" " + _toDisplayString(vendorSearch.value ? `Create New "${vendorSearch.value}"` : 'Add New Vendor'), 1)
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["model-value", "search", "items", "loading", "error-messages"]),
                        (showAddVendor.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              _cache[14] || (_cache[14] = _createElementVNode("label", { class: "text-sm font-medium text-gray-700 mb-1 block" }, [
                                _createTextVNode(" Display Name "),
                                _createElementVNode("span", { class: "font-bold" }, "*")
                              ], -1)),
                              _createElementVNode("div", _hoisted_7, [
                                _createVNode(LlTextField, {
                                  modelValue: newVendorName.value,
                                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newVendorName).value = $event)),
                                  placeholder: "e.g., Harris County Hospital",
                                  "hide-details": "auto",
                                  "data-cy": "new-vendor-name",
                                  class: "flex-1"
                                }, null, 8, ["modelValue"]),
                                _createVNode(LlSecondaryButton, {
                                  color: "primary",
                                  loading: creatingVendor.value,
                                  "data-cy": "save-new-vendor",
                                  onClick: saveNewVendor
                                }, {
                                  default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                    _createTextVNode(" Create ", -1)
                                  ]))]),
                                  _: 1
                                }, 8, ["loading"]),
                                _createVNode(LlSecondaryButton, {
                                  "data-cy": "cancel-new-vendor",
                                  onClick: cancelAddVendor
                                }, {
                                  default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                    _createTextVNode(" Cancel ", -1)
                                  ]))]),
                                  _: 1
                                })
                              ])
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_8, [
                        _createVNode(LlSelect, {
                          modelValue: form.lienType,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.lienType) = $event)),
                          label: "Lien Type",
                          "is-required": true,
                          items: _unref(LIEN_TYPE_OPTIONS),
                          "item-title": "label",
                          "item-value": "id",
                          "return-object": false,
                          placeholder: "Select type",
                          "hide-details": "auto",
                          rules: [(v) => !!v || 'Lien type is required'],
                          "data-cy": "lien-type"
                        }, null, 8, ["modelValue", "items", "rules"])
                      ]),
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("div", null, [
                          _createVNode(LlTextField, {
                            modelValue: form.originalAmount,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.originalAmount) = $event)),
                            label: "Original Amount",
                            "is-required": true,
                            type: "number",
                            min: 0.01,
                            step: 0.01,
                            prefix: "$",
                            placeholder: "0.00",
                            "hide-details": "auto",
                            rules: [(v) => (v !== null && v !== '' && Number(v) > 0) || 'Original amount must be greater than $0'],
                            "data-cy": "lien-original-amount"
                          }, null, 8, ["modelValue", "rules"])
                        ]),
                        _createElementVNode("div", null, [
                          _createVNode(LlTextField, {
                            modelValue: form.negotiatedAmount,
                            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.negotiatedAmount) = $event)),
                            label: "Negotiated Amount (optional)",
                            type: "number",
                            min: 0,
                            step: 0.01,
                            prefix: "$",
                            placeholder: "0.00",
                            "hide-details": "auto",
                            rules: negotiatedAmountRules.value,
                            clearable: "",
                            "data-cy": "lien-negotiated-amount"
                          }, null, 8, ["modelValue", "rules"])
                        ])
                      ]),
                      _createVNode(_Transition, {
                        "enter-active-class": "transition-all duration-300 ease-out",
                        "leave-active-class": "transition-all duration-200 ease-in",
                        "enter-from-class": "opacity-0 -translate-y-2 max-h-0",
                        "enter-to-class": "opacity-100 translate-y-0 max-h-24",
                        "leave-from-class": "opacity-100 translate-y-0 max-h-24",
                        "leave-to-class": "opacity-0 -translate-y-2 max-h-0"
                      }, {
                        default: _withCtx(() => [
                          (savingsPreview.value.show)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                (!savingsPreview.value.over)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                      _createElementVNode("div", _hoisted_12, [
                                        _createElementVNode("div", _hoisted_13, [
                                          _createVNode(LlIcon, {
                                            icon: "mdi-piggy-bank-outline",
                                            size: "18",
                                            color: "success"
                                          })
                                        ]),
                                        _createElementVNode("div", null, [
                                          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "text-xs text-green-dark font-medium" }, "Estimated Savings", -1)),
                                          _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(formatCurrency)(savingsPreview.value.amount)), 1)
                                        ])
                                      ]),
                                      _createElementVNode("div", _hoisted_15, [
                                        _createElementVNode("div", _hoisted_16, _toDisplayString(savingsPreview.value.percentage) + "% ", 1),
                                        _cache[16] || (_cache[16] = _createElementVNode("div", { class: "text-[10px] text-green-dark uppercase tracking-wider font-semibold" }, "reduction", -1))
                                      ])
                                    ]))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                      _createElementVNode("div", _hoisted_18, [
                                        _createElementVNode("div", _hoisted_19, [
                                          _createVNode(LlIcon, {
                                            icon: "mdi-alert-outline",
                                            size: "18",
                                            color: "error"
                                          })
                                        ]),
                                        _createElementVNode("div", null, [
                                          _cache[17] || (_cache[17] = _createElementVNode("div", { class: "text-xs text-error font-medium" }, "Over Original Amount", -1)),
                                          _createElementVNode("div", _hoisted_20, " +" + _toDisplayString(_unref(formatCurrency)(savingsPreview.value.amount)), 1)
                                        ])
                                      ]),
                                      _createElementVNode("div", _hoisted_21, [
                                        _createElementVNode("div", _hoisted_22, _toDisplayString(savingsPreview.value.percentage) + "% ", 1),
                                        _cache[18] || (_cache[18] = _createElementVNode("div", { class: "text-[10px] text-error uppercase tracking-wider font-semibold" }, "over", -1))
                                      ])
                                    ]))
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }),
                      _createElementVNode("div", _hoisted_23, [
                        _createVNode(LlTextarea, {
                          modelValue: form.description,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.description) = $event)),
                          label: "Description (optional)",
                          placeholder: descriptionPlaceholder.value,
                          rows: 2,
                          "hide-details": "auto",
                          rules: [(v) => !v || v.length <= 500 || 'Description cannot exceed 500 characters'],
                          "data-cy": "lien-description"
                        }, null, 8, ["modelValue", "placeholder", "rules"]),
                        _createElementVNode("div", _hoisted_24, [
                          _createElementVNode("span", {
                            class: _normalizeClass(["text-xs", form.description.length > 500 ? 'text-error' : 'text-gray-400'])
                          }, _toDisplayString(form.description.length) + "/500", 3)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_25, [
                        _createElementVNode("div", null, [
                          _createVNode(LlDatePicker, {
                            modelValue: dateOfServiceDate.value,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((dateOfServiceDate).value = $event)),
                            label: "Service Start Date (optional)",
                            "data-cy": "lien-date-of-service"
                          }, null, 8, ["modelValue"])
                        ]),
                        _createElementVNode("div", null, [
                          _createVNode(LlDatePicker, {
                            modelValue: dateOfServiceEndDate.value,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((dateOfServiceEndDate).value = $event)),
                            label: "Service End Date (optional)",
                            min: dateOfServiceDate.value,
                            "data-cy": "lien-date-of-service-end"
                          }, null, 8, ["modelValue", "min"])
                        ])
                      ]),
                      (serviceEndDateError.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_26, _toDisplayString(serviceEndDateError.value), 1))
                        : (_openBlock(), _createElementBlock("div", _hoisted_27)),
                      _createElementVNode("div", _hoisted_28, [
                        _createElementVNode("div", null, [
                          _createVNode(LlDatePicker, {
                            modelValue: recordedFilingDateDate.value,
                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((recordedFilingDateDate).value = $event)),
                            label: "Recorded / Filing Date (optional)",
                            "data-cy": "lien-recorded-filing-date"
                          }, null, 8, ["modelValue"])
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_29, [
                        _createVNode(LlTextField, {
                          modelValue: form.county,
                          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.county) = $event)),
                          label: "County / Jurisdiction (optional)",
                          placeholder: "e.g. Denver County",
                          "hide-details": "auto",
                          rules: [(v) => !v || v.length <= 255 || 'County/Jurisdiction is too long (max 255 characters)'],
                          "data-cy": "lien-county"
                        }, null, 8, ["modelValue", "rules"])
                      ])
                    ]),
                    _: 1
                  }, 512))
            ]),
            _createElementVNode("div", _hoisted_30, [
              _createVNode(LlSecondaryButton, {
                disabled: saving.value || __props.loading,
                "data-cy": "lien-cancel",
                onClick: close
              }, {
                default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                  _createTextVNode(" Cancel ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createElementVNode("div", _hoisted_31, [
                _createVNode(LlSecondaryButton, {
                  color: "primary",
                  loading: saving.value,
                  disabled: __props.loading || isPushedToQbo.value,
                  "data-cy": "lien-save",
                  onClick: submit
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString((isEditing.value || __props.loading) ? 'Save Changes' : 'Add Lien'), 1)
                  ]),
                  _: 1
                }, 8, ["loading", "disabled"]),
                (isPushedToQbo.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_32, "Undo push to edit"))
                  : _createCommentVNode("", true)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})